import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CardHeader,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Alert,
    AlertTitle,
    Button,
    Chip,
    Paper
} from '@mui/material';
import ExploreIcon from '@mui/icons-material/Explore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StorageIcon from '@mui/icons-material/Storage';
import CodeIcon from '@mui/icons-material/Code';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

const GraphExplorerWorkflow: React.FC = () => {
    return (
        <Box id="workflow-graph-explorer" sx={{ scrollMarginTop: '80px' }}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ExploreIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">Enhanced App Testing with Graph Explorer</Typography>
                        </Box>
                    }
                    action={
                        <Button
                            size="small"
                            startIcon={<ArrowBackIcon />}
                            onClick={() => {
                                const element = document.getElementById('workflow-navigation');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                }
                            }}
                        >
                            Back to Workflows
                        </Button>
                    }
                />
                <Divider />
                <CardContent>
                    <Typography variant="body2" paragraph>
                        This workflow demonstrates how to use the Graph Explorer tool to retrieve detailed information about your applications
                        and service principals, complementing the App Tester functionality for comprehensive application testing and troubleshooting.
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Prerequisites:</Typography>
                        <List dense disablePadding>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="At least Application.Read.All permissions (for reading application information)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="Application.ReadWrite.All permissions (for more advanced operations)" />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                <ListItemText primary="User.Read permission (already included in most Entra ID app registrations)" />
                            </ListItem>
                        </List>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 1: Access the Graph Explorer Tool
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                AppConfig² provides direct access to a Graph Explorer for making Microsoft Graph API requests:
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="Navigate to Tools > Graph Explorer"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="If prompted, sign in with your Microsoft account"
                                        secondary="This account should have the necessary permissions listed in prerequisites"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText
                                        primary="You'll be presented with the Graph Explorer interface, which includes:"
                                    />
                                </ListItem>
                            </List>

                            <Box sx={{ pl: 4, mt: 1 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 2, borderRadius: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>Request Panel</Typography>
                                            <List dense disablePadding>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="HTTP method selector (GET, POST, etc.)" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="URL input field for Graph endpoints" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Request headers and body sections" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Run button to execute requests" />
                                                </ListItem>
                                            </List>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 2, borderRadius: 1 }}>
                                            <Typography variant="subtitle2" gutterBottom>Response Panel</Typography>
                                            <List dense disablePadding>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Status code and execution time" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Response headers" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Formatted JSON response body" />
                                                </ListItem>
                                                <ListItem>
                                                    <ListItemIcon sx={{ minWidth: 28 }}><CircleIcon sx={{ fontSize: 8 }} /></ListItemIcon>
                                                    <ListItemText primary="Copy and export options" />
                                                </ListItem>
                                            </List>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 2: Retrieve Application Information
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Use Graph Explorer to retrieve detailed information about your applications:
                            </Typography>

                            <Box sx={{ mt: 2, mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Common Application Queries:</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            1. Get all applications in your tenant:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /applications
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns basic information about all application registrations.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            2. Get specific application by ID:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /applications/{'{'}"objectId"{'}'}
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns detailed information about a specific application using its Object ID.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            3. Find application by display name:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /applications?$filter=displayName eq 'App Name'
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Searches for applications with an exact display name match.
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            4. List application owners:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /applications/{'{'}"objectId"{'}'}/owners
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns all owners of a specific application.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            5. Get application with specific permissions:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /applications?$expand=requiredResourceAccess
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns applications with their requested API permissions.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            6. Get deleted applications:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /directory/deletedItems/microsoft.graph.application
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns recently deleted applications (useful for recovery).
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Box sx={{ mt: 2, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <CodeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    Using Graph Query Parameters
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    Enhance your queries with these useful parameters:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="$select: Specify only the properties you need"
                                            secondary="Example: ?$select=id,displayName,appId"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="$filter: Filter results based on property values"
                                            secondary="Example: ?$filter=startsWith(displayName,'Test')"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="$expand: Include related entities"
                                            secondary="Example: ?$expand=owners"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="$top: Limit the number of results"
                                            secondary="Example: ?$top=5"
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 3: Working with Service Principals
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Service principals are the actual instances of applications in your tenant. They are critical for
                                understanding how applications are configured and deployed:
                            </Typography>

                            <Box sx={{ mt: 2, mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Useful Service Principal Queries:</Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            1. Get all service principals:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /servicePrincipals
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Returns all service principals in your tenant.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            2. Find service principal by app ID:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /servicePrincipals?$filter=appId eq {'{'}"appId"{'}'}&$expand=owners
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Finds the service principal for a specific app and includes owner information.
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} lg={6}>
                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            3. Get service principal's assigned permissions:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /servicePrincipals/{'{'}"objectId"{'}'}/oauth2PermissionGrants
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Shows delegated permissions that have been consented to.
                                        </Typography>

                                        <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                            4. Get app role assignments (application permissions):
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                            GET /servicePrincipals/{'{'}"objectId"{'}'}/appRoleAssignments
                                        </Box>
                                        <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                            Shows application permissions that have been granted.
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <AlertTitle>Service Principal vs. Application</AlertTitle>
                                <Typography variant="body2">
                                    Remember the distinction: an <strong>application</strong> is the global definition of your app,
                                    while a <strong>service principal</strong> is the instance of that application in a specific tenant.
                                    For many troubleshooting scenarios, you'll need to examine both.
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 4: Testing and Troubleshooting Authentication with Graph Explorer
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" paragraph>
                                Here are some common troubleshooting scenarios and how to address them using Graph Explorer:
                            </Typography>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Scenario 1: Troubleshooting Sign-in Issues</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Check for conditional access policies affecting your application:"
                                        />
                                    </ListItem>
                                </List>
                                <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                    GET /identity/conditionalAccess/policies
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
                                    This requires Policy.Read.All permissions. Review policies that might be affecting your application.
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Scenario 2: Permission Issues</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Check if a service principal has the permissions it needs:"
                                        />
                                    </ListItem>
                                </List>
                                <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                    GET /servicePrincipals/{'{'}"objectId"{'}'}/appRoleAssignments
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Examine the returned permissions to verify they match what your application needs.
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>Scenario 3: Claims Mapping Policy Verification</Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                                        <ListItemText
                                            primary="Check which claims mapping policies are assigned to your application:"
                                        />
                                    </ListItem>
                                </List>
                                <Box sx={{ bgcolor: '#f5f9ff', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', my: 0.5 }}>
                                    GET /policies/claimsMappingPolicies?$filter=appliesTo/any(obj:obj/id eq {'{'}"objectId"{'}'})
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Replace objectId with your service principal's Object ID to find associated claims policies.
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 1, my: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    <FindInPageIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
                                    Common Troubleshooting Pattern
                                </Typography>
                                <Typography variant="body2" paragraph>
                                    When troubleshooting applications with AppConfig², combine the App Tester and Graph Explorer in this workflow:
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Use App Tester to attempt authentication and identify issues"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Note the application and service principal IDs from the App Tester"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Use these IDs in Graph Explorer to examine detailed configurations"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Make necessary changes via Graph Explorer or the Azure portal"
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemIcon sx={{ minWidth: 28 }}>
                                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>5</Box>
                                        </ListItemIcon>
                                        <ListItemText
                                            primary="Return to App Tester to verify your changes resolved the issue"
                                        />
                                    </ListItem>
                                </List>
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Step 5: Advanced Graph Explorer Techniques
                        </Typography>
                        <Box sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'primary.light', mb: 2 }}>
                            <Typography variant="body2" paragraph>
                                To become more efficient with Graph Explorer in AppConfig², try these advanced techniques:
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <VpnKeyIcon fontSize="small" sx={{ mr: 1 }} />
                                            Batching Requests
                                        </Typography>
                                        <Typography variant="body2" paragraph>
                                            Use $batch to combine multiple requests into a single HTTP request:
                                        </Typography>
                                        <Box sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85rem', overflowX: 'auto' }}>
                                            POST /$batch<br />
                                            Content-Type: application/json<br />
                                            <br />
                                            {`{
                          "requests": [
                            {
                              "id": "1",
                              "method": "GET",
                              "url": "/applications/{id}"
                            },
                            {
                              "id": "2",
                              "method": "GET",
                              "url": "/servicePrincipals?$filter=appId eq '{appId}'"
                            }
                          ]
                        }`}
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                            <StorageIcon fontSize="small" sx={{ mr: 1 }} />
                                            Saving Common Queries
                                        </Typography>
                                        <Typography variant="body2" paragraph>
                                            AppConfig²'s Graph Explorer allows you to save frequently used queries:
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Use the 'Save Query' button to store your common requests"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Create a library of app troubleshooting queries"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon sx={{ minWidth: 28 }}><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                                                <ListItemText
                                                    primary="Load saved queries with just a click"
                                                />
                                            </ListItem>
                                        </List>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Alert severity="warning" sx={{ mt: 3 }}>
                                <AlertTitle>Using POST, PATCH, and DELETE Operations</AlertTitle>
                                <Typography variant="body2">
                                    AppConfig²'s Graph Explorer also supports write operations (POST, PATCH, DELETE) for modifying
                                    applications and service principals. Be extremely cautious when using these operations as they
                                    make immediate changes to your Entra ID environment.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Always test modifications in a non-production environment first, and ensure you have appropriate
                                    backup procedures in place.
                                </Typography>
                            </Alert>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default GraphExplorerWorkflow;